import "./stax.css";

/** What Stax is doing. Each mood maps to a real builder state; see `bubbleMood`. */
export type StaxMood = "idle" | "working" | "attention" | "done" | "problem";

interface StaxProps {
  mood?: StaxMood;
  /** `full` is the hand-drawn character with arms and legs; `mini` is the single-line version for small sizes (under 64px). */
  variant?: "full" | "mini";
  /** Rendered width and height in CSS pixels. */
  size?: number;
  className?: string;
}

/** Stax, the Stack.je mascot: a hand-drawn stack of sticky notes. Decorative; the surrounding UI carries the meaning. */
export function Stax({ mood = "idle", variant = "full", size = 200, className }: StaxProps) {
  return (
    <svg
      className={["stax", `stax--${variant}`, className].filter(Boolean).join(" ")}
      data-mood={mood}
      viewBox="0 0 200 200"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
    >
      {variant === "full" ? <Full /> : <Mini />}
    </svg>
  );
}

function Full() {
  return (
    <>
      <path className="stax-ground stax-ink2" d="M58 184 Q100 180 142 184" />
      <g className="stax-fx stax-fx-bang">
        <path className="stax-ink" d="M176 22 L173 40" style={{ strokeWidth: 4.4 }} />
        <circle cx="172" cy="49" r="2.8" className="stax-fill-ink" />
      </g>
      <g className="stax-fx stax-fx-q">
        <path className="stax-ink" d="M164 30 Q166 20 174 22 Q182 26 174 36 Q170 40 170 46" />
        <circle cx="170" cy="54" r="2.6" className="stax-fill-ink" />
      </g>
      <g className="stax-fx stax-fx-spark">
        <path className="stax-sp stax-ink" d="M30 40 L30 56 M22 48 L38 48" style={{ strokeWidth: 2.8 }} />
        <path className="stax-sp stax-sp2 stax-ink" d="M170 34 L170 48 M163 41 L177 41" style={{ strokeWidth: 2.8 }} />
        <path className="stax-sp stax-sp3 stax-ink" d="M170 150 L170 162 M164 156 L176 156" style={{ strokeWidth: 2.8 }} />
      </g>
      <path className="stax-fx stax-drop stax-ink stax-fill-paper" d="M44 58 Q38 68 44 72 Q50 68 44 58 Z" />
      <g className="stax-body">
        <g className="stax-back">
          <rect x="60" y="50" width="92" height="92" className="stax-ink stax-fill-paper" transform="rotate(7 106 96)" />
          <rect x="54" y="54" width="92" height="92" className="stax-ink stax-fill-paper" transform="rotate(-2 100 100)" />
        </g>
        <path className="stax-ink" d="M78 152 L75 176 M69 177 L77 176 M114 152 L117 176 M114 176 L123 177" />
        <g className="stax-arm-l"><path className="stax-ink" d="M50 114 Q42 126 40 140" /></g>
        <g className="stax-top">
          <rect x="54" y="64" width="92" height="92" className="stax-fill-marker" />
          <rect x="50" y="60" width="92" height="92" className="stax-ink" />
          <g className="stax-boil"><rect x="50" y="60" width="92" height="92" className="stax-ink2" /></g>
          <path className="stax-curl stax-ink stax-fill-paper" d="M142 132 L122 152 L140 150 Z" />
          <path className="stax-scrib stax-ink" pathLength={1} d="M62 136 Q70 131 78 136 Q86 140 94 134" style={{ strokeWidth: 2.6 }} />
          <path className="stax-scrib stax-scrib2 stax-ink" pathLength={1} d="M62 146 Q68 142 74 146 Q80 150 86 145" style={{ strokeWidth: 2.6 }} />
          <path className="stax-tick stax-ink" pathLength={1} d="M104 134 L112 142 L128 124" style={{ strokeWidth: 4 }} />
          <g className="stax-e-normal"><Eye cx={78} cy={98} r={4.8} /><Eye cx={108} cy={98} r={4.8} /></g>
          <g className="stax-e-down"><Eye cx={80} cy={104} r={4} /><Eye cx={106} cy={104} r={4} /></g>
          <g className="stax-e-big"><Eye cx={78} cy={96} r={6.2} glint /><Eye cx={108} cy={96} r={6.2} glint /></g>
          <path className="stax-e-happy stax-ink" d="M72 100 Q78 92 84 100 M102 100 Q108 92 114 100" />
          <g className="stax-e-worried">
            <Eye cx={80} cy={100} r={3.6} />
            <Eye cx={106} cy={100} r={3.6} />
            <path className="stax-ink" d="M72 90 L84 94 M114 90 L102 94" />
          </g>
          <path className="stax-m-smile stax-ink" d="M84 114 Q93 122 102 114" />
          <g className="stax-m-tongue">
            <path className="stax-ink" d="M86 118 L100 118" />
            <path className="stax-ink stax-fill-paper" d="M96 119 Q99 126 102 119" />
          </g>
          <ellipse className="stax-m-o stax-ink stax-fill-ink" cx="93" cy="118" rx="5" ry="6" />
          <path className="stax-m-grin stax-ink stax-fill-ink" d="M82 110 Q93 126 104 110 Z" />
          <path className="stax-m-wobble stax-ink" d="M82 118 Q86 114 90 118 Q94 122 98 118 Q102 114 106 118" />
        </g>
        <g className="stax-arm-r">
          <path className="stax-ink" d="M142 110 Q150 124 152 138" />
          <rect className="stax-pencil stax-ink stax-fill-paper" x="148" y="136" width="8" height="28" />
        </g>
      </g>
    </>
  );
}

function Mini() {
  return (
    <>
      <g className="stax-fx stax-dot">
        <circle cx="176" cy="30" r="17" className="stax-fill-brand" style={{ stroke: "var(--stax-ink)", strokeWidth: 8 }} />
      </g>
      <g className="stax-body">
        <g className="stax-back">
          <g transform="rotate(8 116 90)">
            <rect x="66" y="40" width="100" height="100" className="stax-inks stax-fill-paper" />
            <path className="stax-line stax-inks" pathLength={1} d="M76 54 Q83 50 90 54 Q97 58 104 54 Q111 50 118 54 Q125 58 132 54" style={{ strokeWidth: 8 }} />
          </g>
        </g>
        <g className="stax-top">
          <rect x="40" y="68" width="110" height="110" className="stax-inks stax-fill-marker" />
          <g className="stax-e-normal"><Eye cx={76} cy={116} r={10} /><Eye cx={112} cy={114} r={10} /></g>
          <path className="stax-e-down stax-inks" d="M66 122 L84 122 M102 120 L120 120" />
          <g className="stax-e-big"><Eye cx={76} cy={112} r={13} glint /><Eye cx={112} cy={110} r={13} glint /></g>
          <path className="stax-e-happy stax-inks" d="M64 118 Q74 104 84 118 M102 115 Q112 101 122 115" />
          <g className="stax-e-worried"><Eye cx={80} cy={116} r={7} /><Eye cx={112} cy={118} r={7} /></g>
          <path className="stax-m-smile stax-inks" d="M82 144 Q95 158 110 142" />
          <path className="stax-m-tongue stax-inks" d="M86 148 L108 148" />
          <circle className="stax-m-o stax-fill-ink" cx="96" cy="146" r="9" />
          <path className="stax-m-grin stax-inks stax-fill-ink" d="M78 136 Q95 158 114 134 Z" />
          <path className="stax-m-wobble stax-inks" d="M78 150 Q86 142 94 150 Q102 158 110 150" />
        </g>
        <g className="stax-hand"><path className="stax-pencil stax-inks" d="M150 172 L168 146" style={{ strokeWidth: 14 }} /></g>
      </g>
    </>
  );
}

function Eye({ cx, cy, r, glint = false }: { cx: number; cy: number; r: number; glint?: boolean }) {
  return (
    <g className="stax-eye">
      <circle cx={cx} cy={cy} r={r} className="stax-fill-ink" />
      {glint && <circle cx={cx + r * 0.32} cy={cy - r * 0.32} r={r * 0.32} className="stax-fill-paper" />}
    </g>
  );
}
